// Maps template IDs to their recommended external skills.
// When a user selects a template, the matching skill is auto-installed
// and its content is fetched from GitHub (SKILL.md) at install time.

export interface TemplateSkill {
  /** Skill name (used as identifier) */
  name: string;
  /** GitHub repo owner/name */
  repo: string;
  /** Human-friendly description */
  description: string;
  /** Raw URL to fetch SKILL.md content from */
  skillMdUrl: string;
}

/**
 * Template ID → skill configuration.
 * Only templates with a dedicated external skill are listed.
 * Templates not listed here (e.g. "none", "vanilla", "playcanvas", "godot") rely on
 * the built-in frontend-design skill or framework instructions alone.
 */
export const TEMPLATE_SKILL_MAP: Record<string, TemplateSkill> = {
  rust: {
    name: 'rust-wasm',
    repo: 'pluginagentmarketplace/custom-plugin-rust',
    description:
      'Rust WebAssembly — wasm-pack, wasm-bindgen, browser integration, and WASM optimization.',
    skillMdUrl:
      'https://raw.githubusercontent.com/pluginagentmarketplace/custom-plugin-rust/main/skills/rust-wasm/SKILL.md',
  },
  go: {
    name: 'golang-pro',
    repo: 'jeffallan/claude-skills',
    description: 'Go/Golang development patterns, concurrency, and best practices.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/golang-pro/SKILL.md',
  },
  flutter: {
    name: 'flutter-expert',
    repo: 'jeffallan/claude-skills',
    description: 'Flutter/Dart mobile and web development with Material Design.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/flutter-expert/SKILL.md',
  },
  next: {
    name: 'nextjs-developer',
    repo: 'jeffallan/claude-skills',
    description: 'Next.js App Router, SSR/SSG, and React Server Components.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/nextjs-developer/SKILL.md',
  },
  express: {
    name: 'expressjs-development',
    repo: 'manutej/luxor-claude-marketplace',
    description: 'Node.js + Express server development patterns and middleware.',
    skillMdUrl:
      'https://raw.githubusercontent.com/manutej/luxor-claude-marketplace/main/expressjs-development/SKILL.md',
  },
  svelte: {
    name: 'sveltekit',
    repo: 'bobmatnyc/claude-mpm-skills',
    description: 'SvelteKit development with stores, SSR, and component patterns.',
    skillMdUrl:
      'https://raw.githubusercontent.com/bobmatnyc/claude-mpm-skills/main/sveltekit/SKILL.md',
  },
  angular: {
    name: 'angular-architect',
    repo: 'jeffallan/claude-skills',
    description: 'Angular TypeScript development with modules, services, and RxJS.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/angular-architect/SKILL.md',
  },
  laravel: {
    name: 'laravel-specialist',
    repo: 'jeffallan/claude-skills',
    description: 'Laravel PHP framework — Eloquent, Blade, middleware, and Artisan.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/laravel-specialist/SKILL.md',
  },
  flask: {
    name: 'flask',
    repo: 'jezweb/claude-skills',
    description: 'Flask web framework — blueprints, Jinja2 templates, and SQLAlchemy.',
    skillMdUrl: 'https://raw.githubusercontent.com/jezweb/claude-skills/main/flask/SKILL.md',
  },
  fastapi: {
    name: 'fastapi',
    repo: 'jezweb/claude-skills',
    description: 'FastAPI async development — Pydantic models, dependency injection, OpenAPI.',
    skillMdUrl: 'https://raw.githubusercontent.com/jezweb/claude-skills/main/fastapi/SKILL.md',
  },
  django: {
    name: 'django-expert',
    repo: 'jeffallan/claude-skills',
    description: 'Django full-stack development — ORM, views, templates, and admin.',
    skillMdUrl:
      'https://raw.githubusercontent.com/jeffallan/claude-skills/main/django-expert/SKILL.md',
  },
  three: {
    name: 'threejs',
    repo: 'mrgoonie/claudekit-skills',
    description: 'Three.js 3D scenes, shaders, post-processing, and WebGL patterns.',
    skillMdUrl: 'https://raw.githubusercontent.com/mrgoonie/claudekit-skills/main/threejs/SKILL.md',
  },
  ci: {
    name: 'cicd-expert',
    repo: 'martinholovsky/claude-skills-generator',
    description: 'CI/CD pipeline design — GitHub Actions, Docker, deployment strategies.',
    skillMdUrl:
      'https://raw.githubusercontent.com/martinholovsky/claude-skills-generator/main/cicd-expert/SKILL.md',
  },
  vue: {
    name: 'vue-best-practices',
    repo: 'vuejs-ai/skills',
    description: 'Vue.js 3 Composition API, Pinia, and component design patterns.',
    skillMdUrl:
      'https://raw.githubusercontent.com/vuejs-ai/skills/main/vue-best-practices/SKILL.md',
  },
  // Note: 'python', 'none', 'vanilla', 'shadcn', 'react', 'playcanvas', 'godot', 'godot3d'
  // are not mapped here — they rely on built-in framework instructions or the frontend-design skill.
  // The nodejs-best-practices skill (davila7/claude-code-templates) is available for manual activation.
};

/**
 * Get the skill config for a given template ID, or null if no skill is mapped.
 */
export function getTemplateSkill(templateId: string): TemplateSkill | null {
  return TEMPLATE_SKILL_MAP[templateId] || null;
}

/**
 * Get all template IDs that have a mapped skill.
 */
export function getTemplatesWithSkills(): string[] {
  return Object.keys(TEMPLATE_SKILL_MAP);
}
